import React, { useState } from 'react';
import {
  Zap, Inbox, LayoutGrid, ListChecks, Network, BarChart3, Compass, Briefcase,
  Brain, Building2, UserPlus, FlaskConical, ShieldCheck, FileSearch, Crown,
  CreditCard, Settings as SettingsIcon, Sun, User, ChevronLeft, X, Smartphone, Monitor,
} from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * AppShellShowcase
 * ----------------
 * Static, simplified previews of the WHOLE-APP shell so the design system
 * documents the structure that AppPage variants slot into.
 *
 * Two showcase sections:
 *   1. "App Shell — 4 variants"  — page / expanded sidebar / collapsed sidebar /
 *      both with the right-side drawer open.
 *   2. "Left panel anatomy"      — labelled regions of the left panel
 *      (logo+AI, The Deck, modules, footer with appearance/profile/collapse).
 */

// ----------------------------------------------------------------------------
// Mock atoms (DS-only — real Sidebar.jsx remains the live source)
// ----------------------------------------------------------------------------

const SIDEBAR_MODULES = [
  { icon: LayoutGrid,    label: 'Daily Dashboard' },
  { icon: ListChecks,    label: 'Tasks' },
  { icon: Network,       label: 'Switchboard' },
  { icon: BarChart3,     label: 'Analytics' },
  { icon: Compass,       label: 'Big Picture' },
  { icon: Briefcase,     label: 'Finance' },
  { icon: Brain,         label: 'Orka AI' },
  { icon: Building2,     label: 'Organisation' },
  { icon: UserPlus,      label: 'Recruitment' },
  { icon: FlaskConical,  label: 'Testing Lab' },
  { icon: ShieldCheck,   label: 'Identity Admin' },
  { icon: FileSearch,    label: 'Architecture Audit' },
  { icon: Crown,         label: 'Owner Console' },
  { icon: CreditCard,    label: 'Billing & Plan' },
  { icon: SettingsIcon,  label: 'Settings' },
];

const LeftPanel = ({ collapsed, labels = false }) => (
  <aside
    className={cn(
      'flex flex-col h-full border-r border-border bg-card flex-shrink-0',
      collapsed ? 'w-12' : 'w-44'
    )}
    data-testid={`shell-left-${collapsed ? 'collapsed' : 'expanded'}`}
  >
    {/* TOP: logo + Orka AI badge */}
    <div className={cn(
      'border-b border-border flex items-center gap-2 p-2',
      collapsed && 'justify-center'
    )}>
      <div className="w-6 h-6 rounded-md bg-violet-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
        O
      </div>
      {!collapsed && (
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-[11px] font-semibold orka-text-primary">Orka</span>
          <Zap className="w-3 h-3 text-violet-500" />
        </div>
      )}
    </div>

    {/* DECK: notices & actions */}
    <div className={cn(
      'border-b border-border p-2',
      collapsed && 'flex justify-center'
    )}>
      <button
        type="button"
        className={cn(
          'flex items-center gap-1.5 rounded-md bg-violet-500/10 text-violet-600 dark:text-violet-400 px-1.5 py-1',
          !collapsed && 'w-full'
        )}
      >
        <span className="relative">
          <Inbox className="w-3.5 h-3.5" />
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
        </span>
        {!collapsed && <span className="text-[10px] font-semibold">The Deck</span>}
        {!collapsed && <span className="ml-auto text-[9px] orka-text-tertiary">3</span>}
      </button>
    </div>

    {/* MODULE MENU */}
    <nav className="flex-1 overflow-hidden py-1">
      <ul className="space-y-0">
        {SIDEBAR_MODULES.slice(0, 9).map((m, i) => (
          <li key={i}>
            <div className={cn(
              'flex items-center gap-1.5 px-2 py-1 text-[10px] orka-text-secondary',
              collapsed && 'justify-center px-1',
              i === 0 && 'bg-violet-500/10 text-violet-600 dark:text-violet-400'
            )}>
              <m.icon className="w-3 h-3 flex-shrink-0" />
              {!collapsed && <span className="truncate">{m.label}</span>}
            </div>
          </li>
        ))}
      </ul>
    </nav>

    {/* FOOTER: appearance + profile + collapse button */}
    <div className="border-t border-border p-1 space-y-0.5">
      <div className={cn(
        'flex items-center gap-1.5 px-1.5 py-1 text-[9px] orka-text-tertiary',
        collapsed && 'justify-center'
      )}>
        <Sun className="w-3 h-3 flex-shrink-0" />
        {!collapsed && (
          <>
            <span>Appearance</span>
            <span className="ml-auto">Auto</span>
          </>
        )}
      </div>
      <div className={cn(
        'flex items-center gap-1.5 px-1.5 py-1 text-[9px] orka-text-secondary',
        collapsed && 'justify-center'
      )}>
        <div className="w-3.5 h-3.5 rounded-full bg-muted flex items-center justify-center">
          <User className="w-2 h-2" />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">Test Admin</div>
          </div>
        )}
      </div>
      <button
        type="button"
        className={cn(
          'w-full flex items-center gap-1 px-1.5 py-1 text-[9px] orka-text-tertiary border-t border-border mt-0.5 pt-1.5',
          collapsed && 'justify-center'
        )}
      >
        <ChevronLeft className={cn('w-3 h-3 transition-transform', collapsed && 'rotate-180')} />
        {!collapsed && <span>Collapse</span>}
      </button>
    </div>

    {/* Annotation labels (only shown in anatomy mode) */}
    {labels && (
      <div className="absolute -right-2 top-2 translate-x-full pointer-events-none w-44 space-y-3 text-[10px] orka-text-tertiary">
        <Tag y={6} label="Logo + Orka AI" />
        <Tag y={48} label="The Deck (notices & actions)" />
        <Tag y={104} label="Modules menu" />
        <Tag y={232} label="Footer: Appearance · Profile · Collapse" />
      </div>
    )}
  </aside>
);

const Tag = ({ y, label }) => (
  <div
    className="absolute left-2 px-1.5 py-0.5 rounded bg-violet-500 text-white whitespace-nowrap"
    style={{ top: y }}
  >
    {label}
  </div>
);

const FakePageBody = ({ children }) => (
  <main className="flex-1 min-w-0 flex flex-col overflow-hidden bg-background">
    <header className="h-8 border-b border-border flex items-center px-3 gap-2 flex-shrink-0">
      <div className="text-[11px] font-semibold orka-text-primary">Page title</div>
      <div className="ml-auto flex items-center gap-1">
        <div className="h-5 w-12 rounded bg-muted" />
        <div className="h-5 w-5 rounded bg-muted" />
      </div>
    </header>
    <div className="flex-1 overflow-auto p-3 space-y-2">{children}</div>
  </main>
);

const DummyRows = ({ rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-7 rounded-md bg-muted/40 border border-border/50" />
    ))}
  </>
);

const RightDrawer = ({ open, children, label = 'Drawer' }) => (
  <aside
    className={cn(
      'flex-shrink-0 border-l border-border bg-card overflow-hidden transition-[width] duration-200',
      open ? 'w-44' : 'w-0'
    )}
    data-testid={`shell-right-drawer-${open ? 'open' : 'closed'}`}
  >
    {open && (
      <div className="h-full flex flex-col">
        <div className="h-8 border-b border-border flex items-center justify-between px-2 flex-shrink-0">
          <span className="text-[10px] font-semibold orka-text-primary">{label}</span>
          <X className="w-3 h-3 orka-text-tertiary" />
        </div>
        <div className="flex-1 p-2 space-y-1.5 overflow-auto">
          {children || (
            <>
              <div className="h-4 rounded bg-muted/40" />
              <div className="h-4 rounded bg-muted/40 w-3/4" />
              <div className="h-4 rounded bg-muted/40" />
              <div className="h-12 rounded bg-muted/30" />
            </>
          )}
        </div>
      </div>
    )}
  </aside>
);

// ----------------------------------------------------------------------------
// Variant frame
// ----------------------------------------------------------------------------

const ShellFrame = ({ children, label, mobile }) => (
  <div className="space-y-2" data-testid={`shell-frame-${label}`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold orka-text-tertiary">
      {mobile ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
      <span>{label}</span>
    </div>
    <div
      className="rounded-lg border border-border overflow-hidden bg-background flex"
      style={{ width: mobile ? 320 : '100%', height: 320, maxWidth: '100%' }}
    >
      {children}
    </div>
  </div>
);

// ============================================================================
// SECTION 1 — App Shell variants
// ============================================================================

export function AppShellVariantsShowcase() {
  return (
    <div data-testid="ds-section-app-shell-variants" className="space-y-6">
      <p className="text-xs orka-text-tertiary leading-relaxed">
        The whole-app shell is the canvas every <strong>App Page</strong> sits inside.
        It has 4 modes by combining left-panel state and right-drawer state.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* A — Expanded sidebar, no drawer */}
        <ShellFrame label="A · Expanded sidebar">
          <LeftPanel collapsed={false} />
          <FakePageBody>
            <DummyRows />
          </FakePageBody>
          <RightDrawer open={false} />
        </ShellFrame>

        {/* B — Collapsed sidebar, no drawer */}
        <ShellFrame label="B · Collapsed sidebar">
          <LeftPanel collapsed />
          <FakePageBody>
            <DummyRows />
          </FakePageBody>
          <RightDrawer open={false} />
        </ShellFrame>

        {/* C — Expanded sidebar + drawer */}
        <ShellFrame label="C · Expanded + right drawer">
          <LeftPanel collapsed={false} />
          <FakePageBody>
            <DummyRows rows={4} />
          </FakePageBody>
          <RightDrawer open label="Approvals" />
        </ShellFrame>

        {/* D — Collapsed sidebar + drawer */}
        <ShellFrame label="D · Collapsed + right drawer">
          <LeftPanel collapsed />
          <FakePageBody>
            <DummyRows rows={4} />
          </FakePageBody>
          <RightDrawer open label="Floating Chat" />
        </ShellFrame>
      </div>

      <div className="rounded-md border border-border p-3 text-[11px] orka-text-secondary leading-relaxed bg-muted/30">
        <strong>Sizing:</strong> expanded sidebar <code className="px-1 bg-muted rounded">w-56</code> ·
        collapsed <code className="px-1 bg-muted rounded">w-12</code> ·
        right drawer typically <code className="px-1 bg-muted rounded">w-80–w-96</code> (slides over on mobile).
        The page body always fills the remaining width with internal scrolling.
      </div>
    </div>
  );
}

// ============================================================================
// SECTION 2 — Left Panel Anatomy
// ============================================================================

export function LeftPanelAnatomyShowcase() {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div data-testid="ds-section-app-shell-anatomy" className="space-y-5">
      <p className="text-xs orka-text-tertiary leading-relaxed">
        The left panel has 4 stacked regions. Toggle the preview to see how each region
        compresses when the panel collapses.
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className={cn(
            'h-7 px-3 rounded text-[11px] border',
            !collapsed ? 'bg-violet-500 text-white border-violet-500' : 'border-border orka-text-secondary hover:bg-muted'
          )}
          data-testid="anatomy-show-expanded"
        >
          Expanded
        </button>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className={cn(
            'h-7 px-3 rounded text-[11px] border',
            collapsed ? 'bg-violet-500 text-white border-violet-500' : 'border-border orka-text-secondary hover:bg-muted'
          )}
          data-testid="anatomy-show-collapsed"
        >
          Collapsed
        </button>
      </div>

      {/* Anatomy with labelled regions */}
      <div className="rounded-lg border border-border bg-background p-6 overflow-x-auto">
        <div className="flex justify-center min-w-[600px]">
          <div className="relative" style={{ height: 360 }}>
            <LeftPanel collapsed={collapsed} />
            {/* Annotation arrows + labels (positioned to right of panel) */}
            <div className="absolute left-full top-0 ml-6 w-64 h-full pointer-events-none text-[10px]">
              <AnnotationLine top={20}  label="① Logo + Orka AI" hint="Brand tag · always visible" />
              <AnnotationLine top={70}  label="② The Deck" hint="Notices & actions · counter shows pending" />
              <AnnotationLine top={150} label="③ Modules menu" hint="Per-org permitted modules · active row highlighted" />
              <AnnotationLine top={250} label="④ Appearance" hint="Theme picker · light / dark / auto" />
              <AnnotationLine top={282} label="⑤ Profile" hint="Account · expands to Settings" />
              <AnnotationLine top={314} label="⑥ Collapse" hint="Toggles ① – ⑤ to icon-only mode" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const AnnotationLine = ({ top, label, hint }) => (
  <div className="absolute" style={{ top, left: 0 }}>
    <div className="flex items-start gap-2">
      <div className="w-6 h-px bg-violet-400 mt-2" />
      <div>
        <div className="font-semibold orka-text-primary">{label}</div>
        <div className="orka-text-tertiary leading-snug">{hint}</div>
      </div>
    </div>
  </div>
);

export default { AppShellVariantsShowcase, LeftPanelAnatomyShowcase };
